import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import moment from "moment";
import { sortBy } from "lodash";
import * as callActions from "../../../actions/callActions";

import CallService from "../../../services/callService";
import { customToast as toast } from "../../../utils";
import SourceSideFilters from "./SourceSideFilters";
import CallList from "./CallList";

const DEFAULT_FILTERS = {
  customer: [],
  project: [],
  assigned_to: {},
  stage: [],
  call_type: [],
  department: {},
  arrival_date: null,
  completed_date: null,
  workplace: [],
  service: []
};

const SourceSide = ({
  users,
  departments,
  onCallClick,
  calls,
  onSetSourceCalls,
  onSelectAll,
  needRefetch,
  setNeedRefetch,
  setAllSelected,
  allSelected,
  setCustomerId
}) => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const handleChangeFilters = (field, value) => {
    const f = { ...filters };
    f[field] = value;

    if (field === "assigned_to") {
      // get user departments for assigned_department filter
      const u = users.find((user) => user._id === value.value);
      const department = departments.find(
        (dep) => dep._id === u.department._id
      );
      f.assigned_to = value;
      f.department = { value: department._id, label: department.description };
    }
    if (field === "customer") {
      setCustomerId(value.value);
      f.project = [];
    }
    setFilters(f);
  };

  const handleChangeDate = (startDate, endDate) => {
    const newFilters = {
      ...filters,
      ...(startDate && {
        arrival_date: moment(startDate).startOf("day")
      }),
      ...(endDate && { completed_date: moment(endDate).endOf("day") })
    };
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    onSetSourceCalls([]);
    setTotalCount(0);
  };

  const handleCallClick = (call) => {
    const copy = [...calls];
    const index = copy.findIndex((c) => c._id === call._id);
    copy[index] = { ...copy[index], highlight: true };
    onSetSourceCalls(copy);
    onCallClick(call);
  };

  const handleFetchCalls = useCallback(async () => {
    try {
      setLoading(true);
      const query = {
        filtered: {
          ...filters,
          service:
            filters.service && filters.service.value ? [filters.service] : [],
          customer: [filters.customer],
          project: [filters.project],
          arrival_date: [filters.arrival_date, filters.completed_date],
          completed_date: null,
          department: [filters.department],
          assigned_to:
            filters.assigned_to && filters.assigned_to.value
              ? [filters.assigned_to]
              : []
        },
        page: 0,
        pageSize: 250,
        sorted: [
          {
            id: "arrival_date",
            asc: true
          }
        ],
        isPopulated: true
      };
      const { calls: queryCalls, count } =
        await CallService.getCallsForMigration(query);
      const sortedCalls = sortBy(queryCalls, "asc");
      onSetSourceCalls(sortedCalls);
      setTotalCount(count[0]?.count);
      setNeedRefetch(false);
    } catch (err) {
      if (err.message === "Request failed with status code 504") {
        toast.error(
          `${err.message}. You might need to have more specific filters`
        );
      } else {
        toast.error(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, onSetSourceCalls, setNeedRefetch]);

  const handleAllSelected = (selected) => {
    setAllSelected(selected);
    onSelectAll(selected);
  };

  const handleSortCalls = (field, direction) => {
    onSetSourceCalls((prev) => {
      const newCalls = sortBy(prev, [field]);
      if (direction === "desc") {
        return newCalls.reverse();
      }
      return newCalls;
    });
  };

  useEffect(() => {
    if (needRefetch) {
      handleFetchCalls();
    }
  }, [handleFetchCalls, needRefetch]);

  return (
    <div>
      <h4 style={{ paddingLeft: 15 }}>Source</h4>
      <SourceSideFilters
        filters={filters}
        onChangeFilters={handleChangeFilters}
        onChangeDate={handleChangeDate}
        onResetFilters={handleResetFilters}
        onSelectAll={handleAllSelected}
        setAllSelected={setAllSelected}
        allSelected={allSelected}
        grabbed={calls.length}
        totalCount={totalCount}
        loading={loading}
        onGetCallsClick={handleFetchCalls}
      />
      <div className="content-wrapper overflowX">
        <CallList
          onCallClick={handleCallClick}
          onSortedChange={handleSortCalls}
          calls={calls}
          loading={loading}
          allSelected={allSelected}
          heightDiff={560}
          noRowsRenderer={() => (
            <div className="no-rows-selected">
              <h3>No rows selected</h3>
            </div>
          )}
        />
      </div>
    </div>
  );
};

SourceSide.propTypes = {
  users: PropTypes.array,
  departments: PropTypes.array,
  onCallClick: PropTypes.func,
  calls: PropTypes.array,
  onSetSourceCalls: PropTypes.func,
  onSelectAll: PropTypes.func,
  needRefetch: PropTypes.bool,
  setNeedRefetch: PropTypes.func,
  setAllSelected: PropTypes.func,
  allSelected: PropTypes.bool,
  setCustomerId: PropTypes.func
};

SourceSide.defaultProps = {
  users: [],
  departments: [],
  onCallClick: () => null,
  calls: [],
  onSetSourceCalls: () => null,
  onSelectAll: () => null,
  setCustomerId: () => null
};

function mapStatesToProps(state) {
  return {
    users: state.reducers.users,
    allUsers: state.reducers.allUsers,
    departments: state.reducers.departments
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...callActions
      },
      dispatch
    )
  };
}

export default connect(mapStatesToProps, mapDispatchToProps)(SourceSide);
